import { useAtomValue, useSetAtom } from 'jotai';

import { useDeepEffect, useDeepMemo } from '@crossnokaye/ux-common';

import { EquipmentBrowserSection } from '@repo/sdk/models/components';

import {
  equipmentPoolAtom,
  identifiersAtom,
} from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';
import {
  useGetEquipmentBrowserConfig,
  useGetEquipmentBrowserView,
} from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

const SelectedEquipmentState = {
  identifiersAtom,
  equipmentPoolAtom,
};

/**
 * Manages the equipment pool based on current view and section selection.
 *
 * An equipment pool is the filtered collection of equipment items available for
 * navigation within the current context. It determines which equipment can be
 * browsed using next/previous controls and establishes the sibling relationships
 * for equipment navigation.
 *
 * The pool is built by:
 * - Fetching all equipment from the selected view
 * - Filtering by section order if a specific section is selected
 * - Falling back to all view equipment if no section is specified
 *
 * This filtered pool enables circular navigation and ensures users only navigate
 * through contextually relevant equipment items.
 *
 * @param dispatch - Reducer dispatch function for equipment selection actions
 * @param identifiers - Current navigation identifiers (viewId, sectionId, equipmentId)
 * @returns Object with loading state
 */
export const useEquipmentPool = () => {
  const identifiers = useAtomValue(SelectedEquipmentState.identifiersAtom);
  const setEquipmentPool = useSetAtom(SelectedEquipmentState.equipmentPoolAtom);

  const { agentId } = useCurrentFacility();
  const { data: equipmentBrowserConfig } = useGetEquipmentBrowserConfig({ agentId });
  const { data: equipmentBrowserViewResponse } = useGetEquipmentBrowserView({
    agentId,
    viewId: identifiers?.viewId ?? equipmentBrowserConfig?.defaultView,
  });

  const sections = equipmentBrowserViewResponse?.view?.sections;
  const summaryConfigs = equipmentBrowserViewResponse?.summaryConfigs;

  const section: EquipmentBrowserSection | undefined = useDeepMemo(() => {
    // First try to find section by explicit sectionId
    if (identifiers?.sectionId) {
      return sections?.find(
        (section) => section.name === identifiers?.sectionId // TODO: change to id.  currently in the bff all sections have the same id.
      );
    }

    // If no sectionId but we have an equipmentId, find the section containing that equipment
    if (identifiers?.equipmentId) {
      return sections?.find((section) => section.order.includes(identifiers.equipmentId!));
    }

    return undefined;
  }, [sections, identifiers?.sectionId, identifiers?.equipmentId]);

  // update the equipment pool based on the order of the equipment in the current section.
  // if no sections provided then we will use all of the equipment returned for the current view.
  useDeepEffect(() => {
    let equipmentPool =
      summaryConfigs?.filter((summary) => {
        return section ? section?.order.includes(summary.referenceId) : true;
      }) ?? [];

    // Sort by section order if a section is available
    if (section?.order) {
      equipmentPool = equipmentPool.sort((a, b) => {
        const aIndex = section.order.indexOf(a.referenceId);
        const bIndex = section.order.indexOf(b.referenceId);
        return aIndex - bIndex;
      });
    }

    setEquipmentPool(equipmentPool);
  }, [summaryConfigs, section]);

  return {
    isLoading: !equipmentBrowserConfig || !equipmentBrowserViewResponse,
  };
};
